export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    // Modern Clipboard API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }

    // Fallback for older browsers
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed'
    textarea.style.left = '-9999px'
    textarea.style.top = '-9999px'
    document.body.appendChild(textarea)
    textarea.focus()
    textarea.select()

    const success = document.execCommand('copy')
    document.body.removeChild(textarea)

    return success
  } catch (error) {
    console.error('Failed to copy to clipboard:', error)
    return false
  }
}

/**
 * Copy an image to the clipboard
 * @param imageSource - Either a base64 string or an HTMLElement to convert
 * @param mimeType - MIME type for base64 images (e.g., 'image/png')
 * @returns Promise that resolves when copy is complete
 */
export async function copyImageToClipboard(
  imageSource: string | HTMLElement,
  mimeType?: string
): Promise<void> {
  // Check if Clipboard API with image support is available
  if (!navigator.clipboard || !('write' in navigator.clipboard)) {
    throw new Error('Clipboard API not supported in this browser')
  }

  let blob: Blob

  if (typeof imageSource === 'string') {
    // Handle base64 image
    const base64Data = imageSource.replace(/^data:image\/\w+;base64,/, '')
    const binaryData = atob(base64Data)
    const bytes = new Uint8Array(binaryData.length)

    for (let i = 0; i < binaryData.length; i++) {
      bytes[i] = binaryData.charCodeAt(i)
    }

    blob = new Blob([bytes], { type: mimeType || 'image/png' })
  } else {
    // Handle HTML element - convert to image using html-to-image
    const { toPng } = await import('html-to-image')
    const dataUrl = await toPng(imageSource, {
      quality: 0.95,
      pixelRatio: 2,
      backgroundColor: 'transparent'
    })

    // Convert data URL to blob
    const response = await fetch(dataUrl)
    blob = await response.blob()
  }

  // Create ClipboardItem and write to clipboard
  const clipboardItem = new ClipboardItem({
    [blob.type]: blob
  })

  await navigator.clipboard.write([clipboardItem])
}
